import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { userService, CreateUserPayload, UpdateUserPayload } from '../../services/users/userService'
import { useToast } from '../../contexts/ToastContext'
import { ResetUserPasswordPayload } from '../../types'
import { ApiError } from '../../utils/errorHandler'

export const useUsers = (params?: {
  page?: number
  limit?: number
  search?: string
}) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => userService.getUsers(params),
  })
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: (payload: CreateUserPayload) => userService.createUser(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['bookers'] })
      showToast(data.message || 'User created successfully', 'success')
    },
    onError: (error: any) => {
      showToast(error.response?.data?.message || 'Failed to create user', 'error')
    },
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserPayload }) => 
      userService.updateUser(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['bookers'] })
      showToast(data.message || 'User updated successfully', 'success')
    },
    onError: (error: any) => {
      showToast(error.response?.data?.message || 'Failed to update user', 'error')
    },
  })
}

export const useResetUserPassword = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ResetUserPasswordPayload }) =>
      userService.resetUserPassword(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      showToast(data.message || 'Password reset successfully', 'success')
    },
    onError: (error: ApiError) => {
      showToast(error.message || 'Failed to reset password', 'error')
    },
  })
}

